import { useEffect, useMemo, useState } from "react";
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, AsYouType } from "libphonenumber-js/min";
import { ChevronDown } from "lucide-react";

const flagOf = (cc) => String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

const TZ_COUNTRY = {
  "Europe/Paris": "FR", "Europe/Moscow": "RU", "Europe/Istanbul": "TR", "Europe/London": "GB", "Europe/Berlin": "DE",
  "Europe/Madrid": "ES", "Europe/Rome": "IT", "Europe/Kyiv": "UA", "Europe/Kiev": "UA", "Europe/Warsaw": "PL",
  "Europe/Amsterdam": "NL", "Europe/Brussels": "BE", "Europe/Zurich": "CH", "Europe/Lisbon": "PT", "Europe/Vienna": "AT",
  "Europe/Prague": "CZ", "Europe/Athens": "GR", "Europe/Minsk": "BY", "Europe/Monaco": "MC", "Europe/Luxembourg": "LU",
  "Europe/Dublin": "IE", "Europe/Stockholm": "SE", "Europe/Oslo": "NO", "Europe/Copenhagen": "DK", "Europe/Helsinki": "FI",
  "Europe/Riga": "LV", "Europe/Vilnius": "LT", "Europe/Tallinn": "EE", "Europe/Bucharest": "RO", "Europe/Sofia": "BG",
  "Europe/Belgrade": "RS", "Europe/Budapest": "HU", "Europe/Chisinau": "MD", "Asia/Tbilisi": "GE", "Asia/Yerevan": "AM",
  "Asia/Baku": "AZ", "Asia/Almaty": "KZ", "Asia/Tashkent": "UZ", "Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Jerusalem": "IL",
  "Asia/Bangkok": "TH", "Asia/Singapore": "SG", "Asia/Tokyo": "JP", "Asia/Shanghai": "CN", "Asia/Kolkata": "IN", "Asia/Nicosia": "CY",
  "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US", "America/Los_Angeles": "US", "America/Toronto": "CA",
  "America/Mexico_City": "MX", "America/Sao_Paulo": "BR", "America/Argentina/Buenos_Aires": "AR", "Australia/Sydney": "AU",
  "Africa/Casablanca": "MA", "Africa/Algiers": "DZ", "Africa/Tunis": "TN", "Africa/Cairo": "EG", "Africa/Johannesburg": "ZA",
};

// Salon phones → timezone → browser locale → FR
export function guessCountry({ phones = [], timezone = "" } = {}) {
  const all = getCountries();
  for (const p of phones) {
    if (!p) continue;
    const raw = String(p).trim();
    const pn = parsePhoneNumberFromString(raw.startsWith("+") ? raw : `+${raw.replace(/\D/g, "")}`);
    if (pn?.country) return pn.country;
  }
  if (timezone && TZ_COUNTRY[timezone]) return TZ_COUNTRY[timezone];
  const region = (navigator.language || "").split("-")[1]?.toUpperCase();
  if (region && all.includes(region)) return region;
  return "FR";
}

// value: E.164 string controlled by parent (reset to "" clears the field).
// onChange({ e164, valid, formatted, digits })
export default function PhoneInput({ value, onChange, defaultCountry = "FR", lang = "en", placeholder = "", accent = "#111", testId = "phone" }) {
  const [country, setCountry] = useState(defaultCountry);
  const [text, setText] = useState("");

  useEffect(() => { if (!value) setText(""); }, [value]);
  useEffect(() => { setCountry(defaultCountry); }, [defaultCountry]);

  const countries = useMemo(() => {
    let dn = null;
    try { dn = new Intl.DisplayNames([lang], { type: "region" }); } catch { /* old browser */ }
    return getCountries()
      .map((c) => ({ code: c, dial: getCountryCallingCode(c), name: (dn && dn.of(c)) || c }))
      .sort((a, b) => a.name.localeCompare(b.name, lang));
  }, [lang]);

  const emit = (txt, cc) => {
    const pn = parsePhoneNumberFromString(txt, cc);
    const digits = txt.replace(/\D/g, "");
    onChange({
      e164: pn ? pn.number : (digits ? `+${getCountryCallingCode(cc)}${digits}` : ""),
      valid: !!pn && pn.isValid(),
      formatted: pn ? pn.formatInternational() : "",
      digits,
    });
  };

  const onText = (e) => {
    const v = e.target.value;
    let cc = country;
    if (v.trim().startsWith("+")) {
      const pn = parsePhoneNumberFromString(v);
      if (pn?.country && pn.country !== cc) { cc = pn.country; setCountry(cc); }
    }
    // format only while typing forward — reformatting on delete fights the caret
    const shown = !v.trim().startsWith("+") && v.length > text.length ? new AsYouType(cc).input(v) : v;
    setText(shown);
    emit(shown, cc);
  };

  const onCountry = (e) => {
    const cc = e.target.value;
    setCountry(cc);
    const stripped = text.trim().startsWith("+") ? "" : text;
    setText(stripped);
    emit(stripped, cc);
  };

  return (
    <div className="flex gap-2" data-testid={`${testId}-wrap`}>
      <div className="relative shrink-0 flex items-center gap-1 border border-gray-300 rounded-lg px-2.5 bg-white text-sm text-gray-900 select-none focus-within:ring-2"
        style={{ "--tw-ring-color": accent }}>
        <span className="text-lg leading-none" aria-hidden>{flagOf(country)}</span>
        <span className="font-semibold">+{getCountryCallingCode(country)}</span>
        <ChevronDown size={14} className="text-gray-500" />
        <select value={country} onChange={onCountry} aria-label="Country"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid={`${testId}-country`}>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>{flagOf(c.code)} {c.name} (+{c.dial})</option>
          ))}
        </select>
      </div>
      <input value={text} onChange={onText} type="tel" inputMode="tel" autoComplete="tel-national"
        placeholder={placeholder}
        className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white text-gray-900"
        data-testid={testId} />
    </div>
  );
}
